import Decimal from 'decimal.js';
import type {
  ConfidenceLevel,
  CronosPriceHint,
  InterpretationBasis,
  LedgerClassification,
  LedgerEvent,
  OwnedWallet,
  TokenRegistryEntry,
} from './types';
import { nativeCroEurPerUnit, prefetchCroHistoricDailyRates } from './cryptoPrices';
import {
  applyCronosSwapProceedsDerivation,
  buildCronosProceedsFinalStats,
  markUnresolvedCronosTokenZeroEur,
} from './cronosSwapProceedsDerivation';
import type { CronosProceedsDerivationStats } from './cronosSwapProceedsDerivation';
import { applyMarketplaceProtocolDisposalSuppression } from './cronosMarketplaceProtocolLegs';
import { applyCronosWalletTouchingFilter } from './cronosWalletTouchingFilter';
import { ownedAddressSetFromWallets } from './walletLotContinuity';
import {
  applyStablecoinAnchorsFromTxLegs,
  isUnknownCrc20AwaitingRatio,
  needsCroAnchorPricing,
  runSwapRatioPricingPass,
  runTokenAcquisitionFromPaidDisposalsPass,
} from './croSwapRatioPricing';
import type { FiCsvDiagnosticRow } from './fiDiagnostics';
import { appendCronosFiDiagnostic } from './fiDiagnostics';
import { isCronosIncomePayoutContract, mergeIncomePayerContracts } from './cronosIncomeContracts';
import { fiMetaForCronosInternalRow } from './fiCronosLedger';
import { applyCexDepositCounterpartyClassification } from './cexDepositCounterparty';
import { labelForAddress } from './ownedWallets';
import {
  buildAggregatesByTxHash,
  buildTokenRegistryFromTokentxRows,
  displayTokenSymbolFromExplorerRow,
  normalizeContractAddress,
  normalizeTokenDecimalsFromExplorerRow,
  summarizeAggregate,
  swapReviewFlagsForAggregate,
  txAppearsSuccessfulFromTxlistRows,
  txHashFromExplorerRow,
  type TxHashAggregate,
} from './tokenRegistry';

/**
 * Cronos explorer API does not send CORS headers. In development, `src/setupProxy.js`
 * proxies `/__cronos_explorer_api__/...` to `https://explorer-api.cronos.org/...` (a
 * dedicated prefix avoids the dev server returning index.html for `/mainnet/...`).
 * For production, set `REACT_APP_CRONOS_EXPLORER_BASE` to a proxy you control if needed.
 */
function getCronosExplorerApiBase(): string {
  const fromEnv = process.env.REACT_APP_CRONOS_EXPLORER_BASE?.trim();
  if (fromEnv) return fromEnv.replace(/\/$/, '');
  if (process.env.NODE_ENV === 'development') {
    return '/__cronos_explorer_api__/mainnet/api/v2';
  }
  return 'https://explorer-api.cronos.org/mainnet/api/v2';
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

function fromRaw(value: string, decimals: number): Decimal {
  try {
    return new Decimal(value || '0').div(new Decimal(10).pow(decimals));
  } catch {
    return new Decimal(0);
  }
}

async function fetchExplorerResult(
  params: string,
  apiKey?: string
): Promise<unknown[]> {
  const baseUrl = `${getCronosExplorerApiBase()}?${params}${
    apiKey ? `&apikey=${encodeURIComponent(apiKey)}` : ''
  }`;

  let lastErr = '';
  for (let attempt = 0; attempt < 12; attempt++) {
    const res = await fetch(baseUrl);
    const text = await res.text();

    if (res.status === 429) {
      const ra = res.headers.get('Retry-After');
      let waitMs = ra
        ? Math.min(parseInt(ra, 10) * 1000, 120000)
        : Math.min(600 * 2 ** attempt, 60000);
      if (!Number.isFinite(waitMs) || waitMs < 400) waitMs = 600 * 2 ** attempt;
      await sleep(waitMs);
      lastErr = 'HTTP 429 Too Many Requests from Cronos explorer';
      continue;
    }

    let data: { result?: unknown; status?: string; message?: string };
    try {
      data = JSON.parse(text) as { result?: unknown; status?: string; message?: string };
    } catch {
      const hint = text.trimStart().startsWith('<')
        ? ' (got HTML — check dev proxy / __cronos_explorer_api__ in setupProxy.js)'
        : '';
      throw new Error(
        `Cronos explorer response is not JSON${hint}. HTTP ${res.status}. ${text.slice(0, 160).replace(/\s+/g, ' ')}${text.length > 160 ? '…' : ''}`
      );
    }
    if (!res.ok) {
      throw new Error(
        `Cronos explorer HTTP ${res.status}: ${data.message ?? text.slice(0, 200)}`
      );
    }
    const r = data.result;
    return Array.isArray(r) ? r : [];
  }
  throw new Error(
    lastErr ||
      'Cronos explorer: too many rate-limit retries — wait a minute and try again, or reduce history size.'
  );
}

async function fetchAllTokentx(
  address: string,
  apiKey?: string,
  pageDelayMs = 450
): Promise<Record<string, unknown>[]> {
  const out: Record<string, unknown>[] = [];
  let page = 1;
  while (true) {
    if (page > 1 && pageDelayMs > 0) await sleep(pageDelayMs);
    const batch = (await fetchExplorerResult(
      `module=account&action=tokentx&address=${encodeURIComponent(
        address
      )}&page=${page}&offset=1000&sort=asc`,
      apiKey
    )) as Record<string, unknown>[];
    if (batch.length === 0) break;
    out.push(...batch);
    if (batch.length < 1000) break;
    page++;
  }
  return out;
}

async function fetchAllTxlist(
  address: string,
  apiKey?: string,
  pageDelayMs = 450
): Promise<Record<string, unknown>[]> {
  const out: Record<string, unknown>[] = [];
  let page = 1;
  while (true) {
    if (page > 1 && pageDelayMs > 0) await sleep(pageDelayMs);
    const batch = (await fetchExplorerResult(
      `module=account&action=txlist&address=${encodeURIComponent(
        address
      )}&page=${page}&offset=1000&sort=asc`,
      apiKey
    )) as Record<string, unknown>[];
    if (batch.length === 0) break;
    out.push(...batch);
    if (batch.length < 1000) break;
    page++;
  }
  return out;
}

/** Internal value transfers (contract execution context). Wallet-scoped; no token list. */
async function fetchAllTxlistInternal(
  address: string,
  apiKey?: string,
  pageDelayMs = 450
): Promise<Record<string, unknown>[]> {
  const out: Record<string, unknown>[] = [];
  let page = 1;
  while (true) {
    if (page > 1 && pageDelayMs > 0) await sleep(pageDelayMs);
    const batch = (await fetchExplorerResult(
      `module=account&action=txlistinternal&address=${encodeURIComponent(
        address
      )}&page=${page}&offset=1000&sort=asc`,
      apiKey
    )) as Record<string, unknown>[];
    if (batch.length === 0) break;
    out.push(...batch);
    if (batch.length < 1000) break;
    page++;
  }
  return out;
}

/**
 * Optional secondary fetch: tokentx with both `address` and `contractaddress`.
 * Use for verification/backfill — not required for the main wallet-first flow.
 */
async function fetchAllTokentxForWalletAndContract(
  walletAddress: string,
  contractAddress: string,
  apiKey?: string,
  pageDelayMs = 450
): Promise<Record<string, unknown>[]> {
  const c = normalizeContractAddress(contractAddress);
  const out: Record<string, unknown>[] = [];
  let page = 1;
  while (true) {
    if (page > 1 && pageDelayMs > 0) await sleep(pageDelayMs);
    const batch = (await fetchExplorerResult(
      `module=account&action=tokentx&contractaddress=${encodeURIComponent(
        c
      )}&address=${encodeURIComponent(walletAddress)}&page=${page}&offset=1000&sort=asc`,
      apiKey
    )) as Record<string, unknown>[];
    if (batch.length === 0) break;
    out.push(...batch);
    if (batch.length < 1000) break;
    page++;
  }
  return out;
}

export type CronosImportProgress = (msg: string) => void;

export function hintForTokentxRow(row: Record<string, unknown>): CronosPriceHint {
  const contract = String(row.contractAddress || row.contract_address || '');
  const sym = String(row.tokenSymbol || row.token_symbol || '?').toUpperCase();
  const blockNum = Number(row.blockNumber ?? row.block_number ?? 0);
  return {
    mode: 'crc20',
    contractAddress: contract.toLowerCase(),
    tokenSymbol: sym,
    blockNumber: Number.isFinite(blockNum) ? blockNum : 0,
  };
}

export function hintForNativeTxRow(row: Record<string, unknown>): CronosPriceHint {
  const blockNum = Number(row.blockNumber ?? row.block_number ?? 0);
  return {
    mode: 'native_cro',
    contractAddress: '',
    tokenSymbol: 'CRO',
    blockNumber: Number.isFinite(blockNum) ? blockNum : 0,
  };
}

function tokentxDedupeKey(row: Record<string, unknown>): string {
  const hash = String(row.hash ?? row.transactionHash ?? '').toLowerCase();
  const li = row.logIndex ?? row.log_index ?? '';
  const c = String(row.contractAddress || row.contract_address || '').toLowerCase();
  return `t:${hash}:${li}:${c}`;
}

function nativeTxDedupeKey(row: Record<string, unknown>): string {
  const hash = String(row.hash ?? '').toLowerCase();
  const from = String(row.from || '').toLowerCase();
  const to = String(row.to || '').toLowerCase();
  const val = String(row.value ?? '0');
  return `n:${hash}:${from}:${to}:${val}`;
}

function internalTxDedupeKey(row: Record<string, unknown>): string {
  const hash = String(row.hash ?? '').toLowerCase();
  const from = String(row.from || '').toLowerCase();
  const to = String(row.to || '').toLowerCase();
  const val = String(row.value ?? '0');
  const trace = String(
    row.traceId ?? row.trace_id ?? row.index ?? row.traceID ?? ''
  );
  return `i:${hash}:${from}:${to}:${val}:${trace}`;
}

export type BuildCronosUnpricedOptions = {
  /** All addresses you control (deduped). Each is fetched; internal moves appear once. */
  wallets: OwnedWallet[];
  apiKey?: string;
  onProgress?: CronosImportProgress;
  explorerPageDelayMs?: number;
  /**
   * Optional UTC ms bounds (inclusive). Applied after fetch — explorer still returns full history per address;
   * we filter locally. Automatic 30-day API windows can be layered on later using block ranges.
   */
  timeFromMs?: number;
  timeToMs?: number;
  /**
   * If true, run optional contract-scoped tokentx fetches using addresses learned from wallet-first results
   * (dedupe merges duplicates). Use for reconciliation / suspected truncation — not required for normal flow.
   */
  enableContractBackfill?: boolean;
  /** Max (wallet × contract) backfill pairs to run when `enableContractBackfill` is on. */
  contractBackfillMaxPairs?: number;
  /**
   * Optional `0x…` or `Label|0x…` lines (NFT / protocol payers). Merged with `CRONOS_INCOME_PAYOUT_CONTRACTS`.
   * Incoming ERC-20 / native CRO from these addresses → taxable income when priced.
   */
  incomePayerAddressLines?: string[];
  /**
   * Centralised exchange **deposit** addresses (shared hot wallets). Not used for explorer fetch — only to
   * reclassify simple transfers to/from your owned wallets as non-taxable internal moves after ingest.
   */
  cexDepositWallets?: OwnedWallet[];
};

export type CronosUnpricedFetchMeta = {
  walletCount: number;
  internalTransferCount: number;
  timeFilteredOut: number;
  dedupedTokenTransfers: number;
  dedupedNativeTx: number;
  dedupedInternalTx: number;
  tokenRegistrySize: number;
  contractBackfillRowsAdded: number;
  /** Synthetic CRO acquisition legs from txlistinternal (router swaps with no WCRO tokentx). */
  routerSwapInternalCroLegsInjected: number;
  /** Rows skipped (tokentx / native txlist) because txlist receipt indicates reverted/failed. */
  revertedOrFailedTxRowsSkipped: number;
  /** Plain transfer rows reclassified as non-taxable internal (CEX deposit counterparty). */
  cexDepositReclassified?: number;
};

export function inTimeWindow(
  ts: number,
  fromMs?: number,
  toMs?: number
): boolean {
  if (fromMs != null && ts < fromMs) return false;
  if (toMs != null && ts > toMs) return false;
  return true;
}

export function classifyTokentxMovement(args: {
  from: string;
  to: string;
  ownedSet: Set<string>;
  wallets: OwnedWallet[];
}): {
  kind: LedgerEvent['kind'];
  classification: LedgerClassification;
  confidence: ConfidenceLevel;
  primary: string;
  explanation: string;
  reviewFlags: string[];
} {
  const { from, to, ownedSet, wallets } = args;
  const oF = ownedSet.has(from);
  const oT = ownedSet.has(to);
  const lf = labelForAddress(wallets, from);
  const lt = labelForAddress(wallets, to);

  if (oF && oT) {
    if (from === to) {
      return {
        kind: 'internal_transfer',
        classification: 'SELF_TRANSFER',
        confidence: 'MEDIUM',
        primary: from,
        explanation: `Same owned address (${lf}); treated as non-taxable self-movement.`,
        reviewFlags: ['same_address_transfer'],
      };
    }
    return {
      kind: 'internal_transfer',
      classification: 'INTERNAL_TRANSFER',
      confidence: 'HIGH',
      primary: from,
      explanation: `Between owned wallets: ${lf} → ${lt} (non-taxable movement).`,
      reviewFlags: [],
    };
  }
  if (oT && !oF) {
    return {
      kind: 'acquisition',
      classification: 'TRANSFER_IN',
      confidence: 'HIGH',
      primary: to,
      explanation: `Incoming to ${lt} from external ${from.slice(0, 10)}….`,
      reviewFlags: [],
    };
  }
  if (oF && !oT) {
    return {
      kind: 'disposal',
      classification: 'TRANSFER_OUT',
      confidence: 'HIGH',
      primary: from,
      explanation: `Outgoing from ${lf} to external ${to.slice(0, 10)}….`,
      reviewFlags: [],
    };
  }
  return {
    kind: 'acquisition',
    classification: 'UNKNOWN_REVIEW',
    confidence: 'LOW',
    primary: from || to,
    explanation:
      'Neither from/to matched owned set after dedupe — keep for manual review (unexpected explorer row).',
    reviewFlags: ['unclear_ownership'],
  };
}

/**
 * Explorer only: ERC-20 + native CRO movements as ledger rows with `eurValue: null`.
 * Wallet-first ingestion: tokentx and txlist by owned address; internal tx for merged context; optional contract backfill.
 * Use `applyCronosHistoricPrices` for taxable rows that carry `cronosPriceHint`.
 */
export type CronosRawExplorerData = {
  tokenTransfers: Record<string, unknown>[];
  nativeTransactions: Record<string, unknown>[];
  internalTransactions: Record<string, unknown>[];
};

export function mergeReviewFlags(
  existing: string[] | undefined,
  extra: string[]
): string[] | undefined {
  const s = new Set([...(existing ?? []), ...extra]);
  return s.size ? Array.from(s) : undefined;
}

/**
 * Router / multicall swaps: WCRO or native CRO to the wallet may only appear in `txlistinternal`, not as an
 * ERC-20 `tokentx`. Without a CRO acquisition row, `applyCronosSwapProceedsDerivation` cannot allocate
 * proceeds to the token disposal. Mirrors `explorerCompiledImport` internal-CRO detection for compiled CSV.
 */
export function injectSyntheticCroAcquisitionFromInternalTx(
  events: LedgerEvent[],
  internalTransactions: Record<string, unknown>[],
  options: {
    ownedSet: Set<string>;
    wallets: OwnedWallet[];
    timeFromMs?: number;
    timeToMs?: number;
    aggregates: Map<string, TxHashAggregate>;
  }
): number {
  const { ownedSet, wallets, timeFromMs, timeToMs, aggregates } = options;
  const internalByHash = new Map<string, Record<string, unknown>[]>();
  for (const row of internalTransactions) {
    const h = txHashFromExplorerRow(row);
    if (!h) continue;
    const arr = internalByHash.get(h) ?? [];
    arr.push(row);
    internalByHash.set(h, arr);
  }

  const byRef = new Map<string, LedgerEvent[]>();
  for (const ev of events) {
    const r = ev.ref?.toLowerCase();
    if (!r) continue;
    const arr = byRef.get(r) ?? [];
    arr.push(ev);
    byRef.set(r, arr);
  }

  let injected = 0;
  for (const [hashKey, sibs] of Array.from(byRef.entries())) {
    const aggRev = aggregates.get(hashKey);
    if (
      aggRev &&
      txAppearsSuccessfulFromTxlistRows(aggRev.txlist) === false
    ) {
      continue;
    }

    const hasCroAcq = sibs.some(
      (e) => e.kind === 'acquisition' && e.asset === 'CRO'
    );
    if (hasCroAcq) continue;

    const hasNonCroDisposal = sibs.some(
      (e) => e.kind === 'disposal' && e.asset !== 'CRO'
    );
    if (!hasNonCroDisposal) continue;

    const internalRows = internalByHash.get(hashKey) ?? [];
    let sum = new Decimal(0);
    let sample: Record<string, unknown> | null = null;
    for (const row of internalRows) {
      const to = String(row.to || '').toLowerCase();
      if (!ownedSet.has(to)) continue;
      const amt = fromRaw(String(row.value ?? '0'), 18);
      if (amt.gt(0)) {
        sum = sum.plus(amt);
        if (!sample) sample = row;
      }
    }
    if (!sum.gt(0) || !sample) continue;

    const tsSec = Number(
      sample.timeStamp ?? sample['timestamp'] ?? 0
    );
    const ts = tsSec * 1000;
    if (!inTimeWindow(ts, timeFromMs, timeToMs)) continue;

    const refDisplay = sibs.find((e) => e.ref)?.ref ?? hashKey;
    const toW = String(sample.to || '').toLowerCase();
    const agg = aggregates.get(hashKey);
    const summ = agg
      ? summarizeAggregate(agg)
      : {
          sourceRecordCount: 0,
          sourceSummary: 'none',
          hasTransactionRecord: false,
          hasTokenTransferRecord: false,
          hasInternalTxRecord: false,
        };

    events.push({
      ts,
      source: 'cronos',
      kind: 'acquisition',
      asset: 'CRO',
      amount: sum,
      eurValue: null,
      ref: refDisplay,
      classification: 'TRANSFER_IN',
      confidence: 'MEDIUM',
      reviewFlags: [
        'swap_internal_native_cro_in',
        'synthetic_router_cro_leg',
      ],
      explanation:
        'CRO received via internal tx (router swap); synthetic acquisition — no WCRO ERC-20 tokentx to wallet.',
      fromAddress: String(sample.from || '').toLowerCase(),
      toAddress: toW,
      primaryWalletAddress: toW,
      primaryWalletLabel: labelForAddress(wallets, toW),
      cronosPriceHint: hintForNativeTxRow(sample),
      interpretationBasis: 'synthetic_from_txlistinternal',
      hasTransactionRecord: summ.hasTransactionRecord,
      hasTokenTransferRecord: summ.hasTokenTransferRecord,
      hasInternalTxRecord: summ.hasInternalTxRecord,
      sourceRecordCount: summ.sourceRecordCount,
      sourceSummary: summ.sourceSummary,
      discoveredFromWalletFetch: true,
      tokenRegistryMatch: false,
      contractBackfillUsed: false,
    });
    injected++;
  }
  return injected;
}

export async function buildCronosLedgerEventsUnpriced(
  options: BuildCronosUnpricedOptions
): Promise<{
  events: LedgerEvent[];
  warnings: string[];
  meta: CronosUnpricedFetchMeta;
  rawExplorer: CronosRawExplorerData;
  tokenRegistry: TokenRegistryEntry[];
  diagnostics: FiCsvDiagnosticRow[];
}> {
  const warnings: string[] = [];
  let events: LedgerEvent[] = [];
  const diagnostics: FiCsvDiagnosticRow[] = [];
  const explorerPageDelay = options.explorerPageDelayMs ?? 450;
  const prog = options.onProgress;
  const apiKey = options.apiKey ?? process.env.REACT_APP_CRONOS_EXPLORER_API_KEY;
  const wallets = options.wallets;
  const timeFromMs = options.timeFromMs;
  const timeToMs = options.timeToMs;

  const emptyMeta: CronosUnpricedFetchMeta = {
    walletCount: 0,
    internalTransferCount: 0,
    timeFilteredOut: 0,
    dedupedTokenTransfers: 0,
    dedupedNativeTx: 0,
    dedupedInternalTx: 0,
    tokenRegistrySize: 0,
    contractBackfillRowsAdded: 0,
    routerSwapInternalCroLegsInjected: 0,
    revertedOrFailedTxRowsSkipped: 0,
  };

  if (!wallets.length) {
    return {
      events: [],
      warnings: ['No owned wallets configured.'],
      meta: emptyMeta,
      rawExplorer: {
        tokenTransfers: [],
        nativeTransactions: [],
        internalTransactions: [],
      },
      tokenRegistry: [],
      diagnostics: [],
    };
  }

  const ownedSet = new Set(wallets.map((w) => w.address.toLowerCase()));
  const incomePayers = mergeIncomePayerContracts(options.incomePayerAddressLines);
  const tokenDedupe = new Map<string, Record<string, unknown>>();
  const nativeDedupe = new Map<string, Record<string, unknown>>();
  const internalDedupe = new Map<string, Record<string, unknown>>();
  const backfillKeys = new Set<string>();
  let timeFilteredOut = 0;
  let revertedOrFailedTxRowsSkipped = 0;

  /** Primary path: tokentx by wallet only (no contract list). */
  prog?.(`Wallet-first: token transfers for ${wallets.length} address(es) (no pre-supplied token list)…`);
  for (const w of wallets) {
    prog?.(`— tokentx: ${w.label} (${w.address.slice(0, 8)}…)…`);
    const tokenRows = await fetchAllTokentx(w.address, apiKey, explorerPageDelay);
    for (const row of tokenRows) {
      const k = tokentxDedupeKey(row);
      if (!tokenDedupe.has(k)) tokenDedupe.set(k, row);
    }
  }
  prog?.(`tokentx unique rows: ${tokenDedupe.size}`);

  const enableBackfill = options.enableContractBackfill === true;
  const maxPairs = options.contractBackfillMaxPairs ?? 25;
  if (enableBackfill && tokenDedupe.size > 0) {
    const preReg = buildTokenRegistryFromTokentxRows(
      Array.from(tokenDedupe.values()),
      ownedSet
    );
    const pairs: { wallet: string; contract: string; weight: number }[] = [];
    for (const w of wallets) {
      const wa = w.address.toLowerCase();
      for (const [c, ent] of Array.from(preReg.entries())) {
        if (ent.ownedWalletsTouched.includes(wa)) {
          pairs.push({ wallet: wa, contract: c, weight: ent.transferCount });
        }
      }
    }
    pairs.sort((a, b) => b.weight - a.weight);
    const take = pairs.slice(0, maxPairs);
    prog?.(`Optional contract-scoped tokentx backfill: ${take.length} pair(s) max…`);
    for (const { wallet, contract } of take) {
      const rows = await fetchAllTokentxForWalletAndContract(
        wallet,
        contract,
        apiKey,
        explorerPageDelay
      );
      for (const row of rows) {
        const k = tokentxDedupeKey(row);
        if (!tokenDedupe.has(k)) {
          tokenDedupe.set(k, row);
          backfillKeys.add(k);
        }
      }
    }
    prog?.(`Backfill merged ${backfillKeys.size} new tokentx row(s).`);
  }

  const tokenTransfers = Array.from(tokenDedupe.values()) as Record<string, unknown>[];
  const registryMap = buildTokenRegistryFromTokentxRows(tokenTransfers, ownedSet);
  const tokenRegistry: TokenRegistryEntry[] = Array.from(registryMap.values()).sort((a, b) =>
    a.contractAddress.localeCompare(b.contractAddress)
  );
  prog?.(`Token registry: ${tokenRegistry.length} contract(s) discovered from transfers.`);

  prog?.(`Normal transactions (txlist) for ${wallets.length} wallet(s)…`);
  for (const w of wallets) {
    prog?.(`— txlist: ${w.label} (${w.address.slice(0, 8)}…)…`);
    const txs = await fetchAllTxlist(w.address, apiKey, explorerPageDelay);
    for (const row of txs) {
      const k = nativeTxDedupeKey(row);
      if (!nativeDedupe.has(k)) nativeDedupe.set(k, row);
    }
  }
  prog?.(`txlist unique rows: ${nativeDedupe.size}`);

  prog?.(`Internal transactions (txlistinternal) for ${wallets.length} wallet(s)…`);
  for (const w of wallets) {
    prog?.(`— internal: ${w.label} (${w.address.slice(0, 8)}…)…`);
    const internalRows = await fetchAllTxlistInternal(w.address, apiKey, explorerPageDelay);
    for (const row of internalRows) {
      const k = internalTxDedupeKey(row);
      if (!internalDedupe.has(k)) internalDedupe.set(k, row);
    }
  }
  prog?.(`Internal tx unique rows: ${internalDedupe.size}`);

  const nativeTransactions = Array.from(nativeDedupe.values()) as Record<string, unknown>[];
  const internalTransactions = Array.from(internalDedupe.values()) as Record<string, unknown>[];

  const aggregates = buildAggregatesByTxHash(
    tokenTransfers,
    nativeTransactions,
    internalTransactions
  );

  for (const row of tokenTransfers) {
    const from = String(row.from || '').toLowerCase();
    const to = String(row.to || '').toLowerCase();
    const contractAddr = normalizeContractAddress(
      String(row.contractAddress || row.contract_address || '')
    );
    const sym = displayTokenSymbolFromExplorerRow(row, contractAddr);
    const dec = normalizeTokenDecimalsFromExplorerRow(row);
    const raw = String(row.value ?? '0');
    const tsSec = Number(row.timeStamp ?? row.timestamp ?? 0);
    const ts = tsSec * 1000;
    const hash = String(row.hash ?? row.transactionHash ?? '');
    const hashKey = txHashFromExplorerRow(row);
    const amt = fromRaw(raw, dec);
    if (amt.lte(0)) continue;

    if (!inTimeWindow(ts, timeFromMs, timeToMs)) {
      timeFilteredOut++;
      continue;
    }

    const aggFail = aggregates.get(hashKey);
    if (
      aggFail &&
      txAppearsSuccessfulFromTxlistRows(aggFail.txlist) === false
    ) {
      revertedOrFailedTxRowsSkipped++;
      continue;
    }

    const meta = classifyTokentxMovement({ from, to, ownedSet, wallets });
    let kind: LedgerEvent['kind'] = meta.kind;
    let classification: LedgerClassification = meta.classification;
    let incomeTag: Partial<LedgerEvent> = {};
    if (
      meta.kind === 'acquisition' &&
      meta.classification === 'TRANSFER_IN' &&
      isCronosIncomePayoutContract(from, incomePayers)
    ) {
      kind = 'income';
      classification = 'INCOME';
      incomeTag = {
        fiTaxCategory: 'TAXABLE_INCOME',
        fiSubreason: 'CRONOS_ALLOWLIST_STAKING_OR_REWARD',
        explanation:
          'Incoming ERC-20 from a configured income/payer address (Cronos allowlist) — taxable income at FMV when priced (FIFO basis = EUR).',
      };
    }
    if (meta.classification === 'UNKNOWN_REVIEW') {
      warnings.push(
        `Review: ERC-20 ${sym} tx ${hash.slice(0, 10)}… — ${meta.explanation}`
      );
    }

    const agg = aggregates.get(hashKey);
    const summ = agg
      ? summarizeAggregate(agg)
      : {
          sourceRecordCount: 0,
          sourceSummary: 'none',
          hasTransactionRecord: false,
          hasTokenTransferRecord: false,
          hasInternalTxRecord: false,
        };
    const swapFlags = agg ? swapReviewFlagsForAggregate(agg, ownedSet) : [];
    const kDedupe = tokentxDedupeKey(row);
    const interpretationBasis: InterpretationBasis = summ.hasTransactionRecord
      ? 'token_transfer_with_tx_context'
      : 'token_transfer_primary';

    const base: LedgerEvent = {
      ts,
      source: 'cronos',
      kind,
      asset: sym === 'WCRO' ? 'CRO' : sym,
      amount: amt,
      eurValue: null,
      ref: hash,
      classification,
      confidence: meta.confidence,
      reviewFlags: mergeReviewFlags(meta.reviewFlags, swapFlags),
      explanation: incomeTag.explanation ?? meta.explanation,
      fromAddress: from,
      toAddress: to,
      primaryWalletAddress: meta.primary,
      primaryWalletLabel: labelForAddress(wallets, meta.primary),
      tokenContractAddress: contractAddr || undefined,
      discoveredFromWalletFetch: true,
      tokenRegistryMatch: contractAddr ? registryMap.has(contractAddr) : false,
      contractBackfillUsed: backfillKeys.has(kDedupe),
      interpretationBasis,
      hasTransactionRecord: summ.hasTransactionRecord,
      hasTokenTransferRecord: summ.hasTokenTransferRecord,
      hasInternalTxRecord: summ.hasInternalTxRecord,
      sourceRecordCount: summ.sourceRecordCount,
      sourceSummary: summ.sourceSummary,
      ...(kind === 'internal_transfer' ? fiMetaForCronosInternalRow(classification) : {}),
      ...incomeTag,
    };

    if (kind === 'internal_transfer') {
      events.push(base);
      appendCronosFiDiagnostic(diagnostics, base);
    } else if (classification === 'UNKNOWN_REVIEW') {
      events.push(base);
      appendCronosFiDiagnostic(diagnostics, base);
    } else {
      events.push({
        ...base,
        cronosPriceHint: hintForTokentxRow(row),
      });
    }
  }

  for (const row of nativeTransactions) {
    const from = String(row.from || '').toLowerCase();
    const to = String(row.to || '').toLowerCase();
    const rawVal = String(row.value ?? '0');
    const tsSec = Number(row.timeStamp ?? 0);
    const ts = tsSec * 1000;
    const hash = String(row.hash ?? '');
    const hashKey = txHashFromExplorerRow(row);
    const amt = fromRaw(rawVal, 18);
    if (amt.lte(0)) continue;

    if (!inTimeWindow(ts, timeFromMs, timeToMs)) {
      timeFilteredOut++;
      continue;
    }

    if (txAppearsSuccessfulFromTxlistRows([row]) === false) {
      revertedOrFailedTxRowsSkipped++;
      continue;
    }

    const meta = classifyTokentxMovement({ from, to, ownedSet, wallets });
    let kindN: LedgerEvent['kind'] = meta.kind;
    let classificationN: LedgerClassification = meta.classification;
    let incomeTagN: Partial<LedgerEvent> = {};
    if (
      meta.kind === 'acquisition' &&
      meta.classification === 'TRANSFER_IN' &&
      isCronosIncomePayoutContract(from, incomePayers)
    ) {
      kindN = 'income';
      classificationN = 'INCOME';
      incomeTagN = {
        fiTaxCategory: 'TAXABLE_INCOME',
        fiSubreason: 'CRONOS_ALLOWLIST_STAKING_OR_REWARD',
        explanation:
          'Incoming native CRO from a configured income/payer address (Cronos allowlist) — taxable income at FMV when priced.',
      };
    }
    if (meta.classification === 'UNKNOWN_REVIEW') {
      warnings.push(
        `Review: native CRO tx ${hash.slice(0, 10)}… — ${meta.explanation}`
      );
    }

    const agg = aggregates.get(hashKey);
    const summ = agg
      ? summarizeAggregate(agg)
      : {
          sourceRecordCount: 0,
          sourceSummary: 'none',
          hasTransactionRecord: false,
          hasTokenTransferRecord: false,
          hasInternalTxRecord: false,
        };
    const swapFlags = agg ? swapReviewFlagsForAggregate(agg, ownedSet) : [];

    const interpretationBasis: InterpretationBasis =
      summ.hasTokenTransferRecord && !summ.hasTransactionRecord
        ? 'merged_evidence_weak'
        : 'native_transaction_primary';

    const base: LedgerEvent = {
      ts,
      source: 'cronos',
      kind: kindN,
      asset: 'CRO',
      amount: amt,
      eurValue: null,
      ref: hash,
      classification: classificationN,
      confidence: meta.confidence,
      reviewFlags: mergeReviewFlags(meta.reviewFlags, swapFlags),
      explanation: incomeTagN.explanation ?? meta.explanation,
      fromAddress: from,
      toAddress: to,
      primaryWalletAddress: meta.primary,
      primaryWalletLabel: labelForAddress(wallets, meta.primary),
      discoveredFromWalletFetch: true,
      tokenRegistryMatch: false,
      contractBackfillUsed: false,
      interpretationBasis,
      hasTransactionRecord: summ.hasTransactionRecord,
      hasTokenTransferRecord: summ.hasTokenTransferRecord,
      hasInternalTxRecord: summ.hasInternalTxRecord,
      sourceRecordCount: summ.sourceRecordCount,
      sourceSummary: summ.sourceSummary,
      ...(kindN === 'internal_transfer' ? fiMetaForCronosInternalRow(classificationN) : {}),
      ...incomeTagN,
    };

    if (kindN === 'internal_transfer') {
      events.push(base);
      appendCronosFiDiagnostic(diagnostics, base);
    } else if (classificationN === 'UNKNOWN_REVIEW') {
      events.push(base);
      appendCronosFiDiagnostic(diagnostics, base);
    } else {
      events.push({
        ...base,
        cronosPriceHint: hintForNativeTxRow(row),
      });
    }
  }

  const routerSwapInternalCroLegsInjected =
    injectSyntheticCroAcquisitionFromInternalTx(events, internalTransactions, {
      ownedSet,
      wallets,
      timeFromMs,
      timeToMs,
      aggregates,
    });

  events.sort((a, b) => a.ts - b.ts);

  let cexDepositReclassified = 0;
  if (options.cexDepositWallets?.length) {
    const cexSet = new Set(
      options.cexDepositWallets.map((w) => w.address.toLowerCase())
    );
    const applied = applyCexDepositCounterpartyClassification(events, {
      ownedAddresses: ownedSet,
      cexDepositAddresses: cexSet,
    });
    events = applied.events;
    cexDepositReclassified = applied.reclassified;
  }

  const internalTransferCount = events.filter((e) => e.kind === 'internal_transfer').length;

  return {
    events,
    warnings,
    meta: {
      walletCount: wallets.length,
      internalTransferCount,
      timeFilteredOut,
      dedupedTokenTransfers: tokenDedupe.size,
      dedupedNativeTx: nativeDedupe.size,
      dedupedInternalTx: internalDedupe.size,
      tokenRegistrySize: tokenRegistry.length,
      contractBackfillRowsAdded: backfillKeys.size,
      routerSwapInternalCroLegsInjected,
      revertedOrFailedTxRowsSkipped,
      cexDepositReclassified,
    },
    rawExplorer: { tokenTransfers, nativeTransactions, internalTransactions },
    tokenRegistry,
    diagnostics,
  };
}

export type ApplyCronosPricesProgress = (msg: string) => void;

/**
 * 1) **Transaction-derived:** USDC/USDT same-tx CRO leg (Frankfurter USD→EUR), then CRO↔token swap ratios.
 * 2) **Fallback:** Bundled daily **CRO/USD** (`cro-usd-max.csv`) → EUR via Frankfurter (`nativeCroEurPerUnit`); swap ratios again.
 * 3) Swap-flow proceeds: USDC/USDT acquisition rows priced (Frankfurter) in swap txs, then incoming EUR
 *    allocation + stable/CRO outgoing fallbacks.
 * 3b) **Buy-side:** same-tx EUR on stable/CRO **disposals** (after step 3) is split across unpriced CRC-20
 *    **acquisitions** so FIFO has acquisition cost when you paid USDC/CRO for a token.
 * 4) Unknown CRC-20 still unpriced: **0 EUR** + flags.
 *
 * **Before step 1:** optional `ownedWallets` — ignore Cronos rows whose transfer does not list any
 * owned address on from/to/primary (contract-only legs). Then wallet-centric swap cleanup —
 * intermediate CRO/WCRO legs, secondary sell legs (largest amount vs incoming stables), optional
 * `CRONOS_MARKETPLACE_FEE_TOKEN_CONTRACTS`.
 */
export async function applyCronosHistoricPrices(
  events: LedgerEvent[],
  options?: {
    /** @deprecated Ignored — CRO/USD comes from bundled CSV. */
    coingeckoDelayMs?: number;
    onProgress?: ApplyCronosPricesProgress;
    /** When set, Cronos rows with no configured owned wallet on from/to/primary are ignored. */
    ownedWallets?: OwnedWallet[];
  }
): Promise<{
  events: LedgerEvent[];
  pricedCount: number;
  stillMissing: string[];
  proceedsDerivationStats: CronosProceedsDerivationStats;
}> {
  const prog = options?.onProgress;
  const stillMissing: string[] = [];
  let pricedCount = 0;

  const working: LedgerEvent[] = events.map((e) => ({ ...e }));

  if (options?.ownedWallets?.length) {
    const wt = applyCronosWalletTouchingFilter(
      working,
      ownedAddressSetFromWallets(options.ownedWallets)
    );
    if (wt.stats.rowsSuppressed > 0) {
      prog?.(
        `Ignored ${wt.stats.rowsSuppressed} Cronos row(s) with no configured owned wallet on from/to/primary (contract-only / out-of-scope legs).`
      );
    }
  }

  const mpSup = applyMarketplaceProtocolDisposalSuppression(working);
  if (mpSup.stats.disposalsSuppressed > 0) {
    prog?.(
      `Wallet-centric swap cleanup: suppressed ${mpSup.stats.disposalsSuppressed} disposal row(s) ` +
        `(intermediate CRO/WCRO: ${mpSup.stats.intermediateCroLegsSuppressed}, secondary sell leg: ${mpSup.stats.secondarySellLegsSuppressed}, optional fee allowlist: ${mpSup.stats.optionalFeeTokenLegsSuppressed}).`
    );
  }

  prog?.('Pricing: transaction-derived (stables + CRO swap ratios, pass 1)…');
  const stableN = await applyStablecoinAnchorsFromTxLegs(working);
  pricedCount += stableN;
  if (stableN > 0) {
    prog?.(`Priced ${stableN} CRO leg(s) from USDC/USDT in same tx (Frankfurter).`);
  }

  const ratio1 = runSwapRatioPricingPass(working);
  pricedCount += ratio1;

  const anchorRows = working.filter(needsCroAnchorPricing);
  const anchorTotal = anchorRows.length;

  await prefetchCroHistoricDailyRates(anchorRows.map((ev) => new Date(ev.ts)), {
    onProgress: prog,
  });

  prog?.('Pricing: bundled CRO/USD → EUR for remaining anchor rows…');
  let anchorIx = 0;
  for (const ev of anchorRows) {
    anchorIx++;
    if (anchorIx === 1 || anchorIx % 50 === 0) {
      prog?.(`CRO/EUR (CSV)… ${anchorIx}/${anchorTotal}`);
    }

    const h = ev.cronosPriceHint!;
    const date = new Date(ev.ts);
    const blockNum = h.blockNumber > 0 ? h.blockNumber : 0;
    const hint = blockNum > 0 ? blockNum : undefined;

    try {
      const unit = await nativeCroEurPerUnit(date, hint);
      if (unit != null) {
        ev.eurValue = unit.mul(ev.amount);
        ev.cronosPriceHint = undefined;
        pricedCount++;
      } else {
        const iso = date.toISOString().slice(0, 10);
        const refShort = ev.ref ? `${ev.ref.slice(0, 10)}…` : '—';
        stillMissing.push(
          `${iso} | ${ev.kind} | ${ev.asset} | CRO anchor unavailable (bundled CSV / Frankfurter) | ref ${refShort}`
        );
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const iso = date.toISOString().slice(0, 10);
      const refShort = ev.ref ? `${ev.ref.slice(0, 10)}…` : '—';
      stillMissing.push(
        `${iso} | ${ev.kind} | ${ev.asset} | CRO anchor error: ${msg} | ref ${refShort}`
      );
    }
  }

  prog?.('Pricing: CRO swap ratios (pass 2, after external CRO anchor)…');
  const ratio2 = runSwapRatioPricingPass(working);
  pricedCount += ratio2;

  prog?.('Pricing: swap-like flow proceeds + outgoing-asset fallbacks…');
  const derivPartial = await applyCronosSwapProceedsDerivation(working);
  pricedCount +=
    derivPartial.disposalProceedsFromReceivedAsset +
    derivPartial.disposalProceedsFromOutgoingFallback +
    derivPartial.stableAcquisitionsPricedForSwap;

  prog?.(
    'Pricing: buy-side CRC-20 acquisitions from paid stable/CRO disposals (same tx, after swap pass)…'
  );
  const buySideAcq = runTokenAcquisitionFromPaidDisposalsPass(working);
  pricedCount += buySideAcq;
  if (buySideAcq > 0) {
    prog?.(
      `Priced ${buySideAcq} token acquisition row(s) from payment disposals in the same transaction.`
    );
  }

  let zeroedUnknown = 0;
  for (const ev of working) {
    if (!isUnknownCrc20AwaitingRatio(ev)) continue;
    markUnresolvedCronosTokenZeroEur(ev);
    zeroedUnknown++;
  }

  const proceedsDerivationStats = buildCronosProceedsFinalStats(
    working,
    { ...derivPartial, tokenAcquisitionsFromPaidDisposals: buySideAcq },
    zeroedUnknown
  );

  const hadCronosHints = events.some(
    (e) =>
      e.source === 'cronos' &&
      e.kind !== 'internal_transfer' &&
      e.eurValue == null &&
      e.cronosPriceHint != null
  );
  prog?.(
    !hadCronosHints && pricedCount === 0 && zeroedUnknown === 0
      ? 'No unpriced Cronos rows with price hints.'
      : `Pricing done — ${pricedCount} row(s) with EUR (tx-derived + fallback), ${zeroedUnknown} unknown token row(s) set to 0 EUR, ${stillMissing.length} CRO anchor fallback failure(s). Swap-like tx: ${proceedsDerivationStats.swapLikeTxCount}; proceeds from received legs: ${proceedsDerivationStats.disposalProceedsFromReceivedAsset}; outgoing fallback: ${proceedsDerivationStats.disposalProceedsFromOutgoingFallback}; buy-side token acq from paid disposals: ${proceedsDerivationStats.tokenAcquisitionsFromPaidDisposals}; zero-proceeds disposals: ${proceedsDerivationStats.zeroProceedsDisposalRows}.`
  );

  return { events: working, pricedCount, stillMissing, proceedsDerivationStats };
}
